import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Circle,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import Tooltip from '../../../../../components/Tooltip';
import { keyframes } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FiCamera,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiPlus,
  FiRefreshCw,
  FiUsers,
} from 'react-icons/fi';
import { Calendar } from '../../../../../components/Date/reactDateRange';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  armarBloques,
  BloqueAgenda,
  EspacioAgenda,
  EstadoReserva,
  esVisita,
  filasSinSitioParaVisita,
  MINUTOS_POR_FILA,
  ocupacionDiaCompleto,
  ReservaCruda,
  useEspacios,
  useMinutoActual,
  useRejilla,
  useReservasEnRango,
} from './useAgenda';
import { filasOcupadas, useSeleccionFranjas } from './useSeleccionFranjas';
import { BotonConfirmarVista, PopOverChangeStatus } from '../estado/CambiarEstado';
import HistorialReserva from '../HistorialReserva';
import TarjetaReserva from '../TarjetaReserva';
import CuantasPersonas from './CuantasPersonas';
import MarcaBeta from '~/components/MarcaBeta';

const MotionBox = motion(Box);

const ALTO_MINIMO = 26; // por debajo no cabe el nombre en una franja suelta
const ALTO_MAXIMO = 52; // por encima el día se convierte en un desierto
const ANCHO_COLUMNA = 178; // mínimo por sala; por debajo no se lee el nombre
const ANCHO_HORAS = 58;
const MARGEN_INFERIOR = 14; // aire bajo la última franja

/**
 * Mide cuánto sitio queda hasta el borde de la ventana.
 *
 * Se mide de verdad en vez de calcularlo con `100vh`: estas vistas viven dentro
 * del modal y arrastran cabecera, título y márgenes que no se pueden adivinar.
 * Sin esto sobraba media pantalla en blanco por debajo del calendario.
 */
const useAltoDisponible = (listo: boolean) => {
  const contenedor = useRef<HTMLDivElement>(null);
  const [disponible, setDisponible] = useState(0);

  useLayoutEffect(() => {
    if (!listo) return undefined;
    const recalcular = () => {
      const caja = contenedor.current;
      if (!caja) return;
      setDisponible(
        Math.max(window.innerHeight - caja.getBoundingClientRect().top - MARGEN_INFERIOR, 200)
      );
    };
    recalcular();
    window.addEventListener('resize', recalcular);
    return () => window.removeEventListener('resize', recalcular);
  }, [listo]);

  return { contenedor, disponible };
};

/**
 * Reparte el alto libre entre las franjas del día en vez de dejarlas fijas: con
 * 26 px por media hora sobraba casi media pantalla en blanco por debajo.
 */
const useAltoFila = (filas: number, listo: boolean) => {
  const { contenedor, disponible } = useAltoDisponible(listo);
  const cabecera = useRef<HTMLDivElement>(null);
  const [altoCabecera, setAltoCabecera] = useState(0);

  useLayoutEffect(() => {
    if (disponible) setAltoCabecera(cabecera.current?.offsetHeight ?? 0);
  }, [disponible]);

  const altoFila = useMemo(() => {
    if (!disponible) return ALTO_MINIMO;
    const reparto = Math.floor((disponible - altoCabecera) / Math.max(filas, 1));
    return Math.min(Math.max(reparto, ALTO_MINIMO), ALTO_MAXIMO);
  }, [disponible, altoCabecera, filas]);

  return { altoFila, disponible, contenedor, cabecera };
};

/**
 * Cada estado tiene su color, no cada sala: la sala ya se sabe por la columna.
 * Lo que hay que ver de un vistazo es en qué punto está cada reserva.
 */
/**
 * Los mismos colores que la tabla de reservas, donde el equipo ya los tiene
 * asociados a cada estado: verde el botón "Confirmar vista" de las pendientes,
 * azul "Hacer Checkin" en las confirmadas, naranja "Hacer Checkout" mientras
 * están en la sala, morado "Completado" al terminar y rojo las canceladas.
 * Inventar una paleta distinta aquí obligaría a aprenderse dos.
 */
const COLORES: Record<EstadoReserva, { bg: string; borde: string; texto: string; nombre: string }> =
  {
    pending: { bg: 'green.50', borde: 'green.500', texto: 'green.800', nombre: 'Sin confirmar' },
    confirmed: { bg: 'blue.50', borde: 'blue.500', texto: 'blue.800', nombre: 'Confirmada' },
    checkin: { bg: 'orange.50', borde: 'orange.400', texto: 'orange.800', nombre: 'En la sala' },
    checkout: { bg: 'purple.50', borde: 'purple.400', texto: 'purple.800', nombre: 'Terminada' },
    cancelled: { bg: 'red.50', borde: 'red.300', texto: 'red.400', nombre: 'Cancelada' },
  };

/** Un hueco libre elegido arrastrando sobre la rejilla del día. */
export type HuecoLibre = {
  espacio: EspacioAgenda;
  fecha: Date;
  /**
   * Vacía en las zonas de día completo: ahí el calendario no elige hora, la
   * pone quien reserva en el formulario (hora de llegada).
   */
  horaInicio?: string;
  /** Solo en las salas que piden hora de fin. */
  horaFin?: string;
  /** Zonas de día completo: sillas que quedan libres ese día. */
  sillasLibres?: number;
  /** En las visitas (sin hora de fin): lo que duran según su configuración. */
  duracionMinutos?: number;
  /** Se rellena en el segundo paso, al preguntar cuánta gente viene. */
  personas?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  /** Si se pasa, la vista de día deja elegir huecos con el ratón. */
  onHuecoElegido?: (hueco: HuecoLibre) => void;
  /**
   * El día que está mirando la tabla de fuera. Cada vez que se abre el
   * calendario arranca en ese día, en vista de día; nunca en el que quedó
   * de la vez anterior ni en el "hoy" de cuando se cargó la página.
   */
  fechaInicial?: Date;
};

/**
 * Aviso cuando Firestore no respondió. Antes, un fallo dejaba el calendario
 * vacío y en silencio, como si no hubiera reservas; eso es peor que un
 * error, porque se toman decisiones con él. Ahora se dice y se puede
 * reintentar sin cerrar nada.
 */
const AvisoDeCarga = ({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) => (
  <Alert
    status='error'
    variant='subtle'
    borderRadius='md'
    m={4}
    w='auto'
    alignItems='center'
    flexWrap='wrap'
    gap={3}
    data-testid='aviso-carga'
  >
    <AlertIcon />
    <AlertDescription flex='1' fontSize='sm'>
      <Text fontWeight='semibold'>El calendario no pudo cargar.</Text>
      <Text color='gray.600'>
        {mensaje} Si vuelve a pasar, avisa a Sontickets: quedó registrado.
      </Text>
    </AlertDescription>
    <Button
      size='sm'
      leftIcon={<FiRefreshCw />}
      colorScheme='red'
      variant='outline'
      onClick={onReintentar}
    >
      Reintentar
    </Button>
  </Alert>
);

/**
 * Pastilla flotante mientras Firestore no confirma los datos desde el
 * servidor. Lo que se ve debajo puede estar incompleto y hay que decirlo: el
 * SDK, cuando se cree sin conexión, entrega listas vacías sin error, y un
 * calendario vacío "normal" es peor que un aviso.
 */
const giro = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AvisoConexion = ({ onReintentar }: { onReintentar: () => void }) => (
  <HStack
    position='absolute'
    top='10px'
    left='50%'
    transform='translateX(-50%)'
    zIndex={8}
    bg='yellow.100'
    color='yellow.900'
    borderWidth='1px'
    borderColor='yellow.300'
    borderRadius='full'
    boxShadow='md'
    px={4}
    py={1.5}
    spacing={3}
    fontSize='sm'
    whiteSpace='nowrap'
    data-testid='aviso-conexion'
  >
    <Icon as={FiRefreshCw} animation={`${giro} 1.4s linear infinite`} />
    <Text>Sin conexión con el servidor · reconectando…</Text>
    <Button size='xs' variant='outline' colorScheme='yellow' onClick={onReintentar}>
      Reintentar
    </Button>
  </HStack>
);

/**
 * El botón de cambio de estado de una reserva, dentro de su tarjeta.
 *
 * Son las mismas piezas que usa la tabla (`estado/CambiarEstado`), con los
 * mismos estados y colores: verde "Confirmar vista", azul "Hacer Checkin",
 * naranja "Hacer Checkout" (con el total, que puede ser 0) y morado
 * "Completado". `compacto` los reduce a su icono para las franjas de media
 * hora; el popover sale por un Portal porque la tarjeta recorta lo que se
 * sale de ella.
 */
const AccionEstado = ({ reserva, compacto }: { reserva: ReservaCruda; compacto: boolean }) => {
  const estado = reserva.status;
  if (estado === 'pending' || !estado) {
    return <BotonConfirmarVista reserva={reserva} compacto={compacto} />;
  }
  if (estado === 'confirmed' || estado === 'checkin' || estado === 'checkout') {
    return (
      <PopOverChangeStatus
        reserva={reserva}
        reservationId={reserva.id}
        datetime={reserva.datetime as never}
        name={reserva.namesAndSurnames ?? reserva.name ?? ''}
        status={estado}
        statusHistory={reserva.statusHistory ?? []}
        numberPeople={reserva.numberPeople ?? 0}
        payment={reserva.payment as never}
        compacto={compacto}
        enPortal
      />
    );
  }
  return null;
};

/**
 * El reloj del historial: quién la hizo, quién la cambió y desde dónde. Es el
 * mismo de la tabla, con el mismo aspecto, y como allí está también en las
 * canceladas, que es cuando más falta hace saberlo.
 */
const BotonHistorial = ({
  reserva,
  compacto,
  onVer,
}: {
  reserva: ReservaCruda;
  compacto: boolean;
  onVer: (reserva: ReservaCruda) => void;
}) => (
  <Tooltip label='Ver historial' placement='top' hasArrow openDelay={300}>
    <IconButton
      aria-label='Ver historial'
      icon={<FiClock />}
      size='xs'
      {...(compacto ? { h: '20px', minW: '20px', fontSize: '12px' } : {})}
      variant='ghost'
      color='gray.500'
      _hover={{ color: 'blue.500', bg: 'blue.50' }}
      onClick={() => onVer(reserva)}
    />
  </Tooltip>
);

/**
 * La cámara: vuelve a abrir la tarjeta de confirmación que vio el cliente,
 * para hacerle una captura de pantalla y mandársela. Igual que en la tabla.
 */
const BotonTarjeta = ({
  reserva,
  compacto,
  onVer,
}: {
  reserva: ReservaCruda;
  compacto: boolean;
  onVer: (reserva: ReservaCruda) => void;
}) => (
  <Tooltip
    label='Tarjeta de confirmación (para captura de pantalla)'
    placement='top'
    hasArrow
    openDelay={300}
  >
    <IconButton
      aria-label='Ver tarjeta de confirmación'
      icon={<FiCamera />}
      size='xs'
      {...(compacto ? { h: '20px', minW: '20px', fontSize: '12px' } : {})}
      variant='ghost'
      color='gray.500'
      _hover={{ color: 'blue.500', bg: 'blue.50' }}
      onClick={() => onVer(reserva)}
    />
  </Tooltip>
);

/**
 * Lo que se puede hacer con una reserva desde su tarjeta: cambiarle el
 * estado, ver su historial y volver a ver su tarjeta de confirmación, uno al
 * lado del otro. La cámara no sale en las canceladas: la tarjeta diría
 * "confirmada".
 */
const AccionesReserva = ({
  reserva,
  compacto,
  onVerHistorial,
  onVerTarjeta,
}: {
  reserva: ReservaCruda;
  compacto: boolean;
  onVerHistorial?: (reserva: ReservaCruda) => void;
  onVerTarjeta?: (reserva: ReservaCruda) => void;
}) => {
  const cancelada = reserva.status === 'cancelled';
  return (
    <HStack spacing={compacto ? 0.5 : 1}>
      {!cancelada && <AccionEstado reserva={reserva} compacto={compacto} />}
      {onVerHistorial && (
        <BotonHistorial reserva={reserva} compacto={compacto} onVer={onVerHistorial} />
      )}
      {!cancelada && onVerTarjeta && (
        <BotonTarjeta reserva={reserva} compacto={compacto} onVer={onVerTarjeta} />
      )}
    </HStack>
  );
};

/** "23:30" → "00:00" solo se usa en el último tramo del día. */
const sumarMediaHora = (hora: string): string => {
  const [h, m] = (hora ?? '00:00').split(':').map(Number);
  const total = h * 60 + m + 30;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(
    2,
    '0'
  )}`;
};

/* ────────────────────────────  bloque  ──────────────────────────── */

const Bloque = ({
  bloque,
  indice,
  altoFila,
  onVerHistorial,
  onVerTarjeta,
}: {
  bloque: BloqueAgenda;
  indice: number;
  altoFila: number;
  onVerHistorial?: (reserva: ReservaCruda) => void;
  onVerTarjeta?: (reserva: ReservaCruda) => void;
}) => {
  const c = COLORES[bloque.estado] ?? COLORES.pending;
  const cancelada = bloque.estado === 'cancelled';
  const anchoCarril = 100 / bloque.carriles;
  const alto = bloque.filas * altoFila - 2;
  // "Corto" es cuestión de píxeles, no de franjas: con filas altas hasta media
  // hora tiene sitio para las dos líneas.
  const corto = bloque.filas * altoFila < 34;
  // Con sitio de sobra el botón va debajo del texto con su nombre, como en la
  // tabla; si no —franjas de media hora, o tarjetas partidas por un choque—,
  // solo su icono a la derecha, para no pedir un clic de más a nadie. Una
  // cancelada solo lleva el reloj, así que va siempre a la derecha.
  const botonConTexto = alto >= 60 && bloque.carriles === 1 && !cancelada;
  const conAcciones = !cancelada || Boolean(onVerHistorial);
  const r = bloque.reserva;
  const telefono = r.phoneNumber ?? r.phone;

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: cancelada ? 0.55 : 1, scale: 1 }}
      transition={{ duration: 0.18, delay: Math.min(indice * 0.012, 0.25) }}
      position='absolute'
      top={`${bloque.filaInicio * altoFila}px`}
      height={`${alto}px`}
      left={`calc(${bloque.carril * anchoCarril}% + 2px)`}
      width={`calc(${anchoCarril}% - 4px)`}
      bg={c.bg}
      borderLeftWidth='3px'
      borderLeftColor={bloque.enConflicto ? 'red.400' : c.borde}
      borderRadius='4px'
      px={1.5}
      py={corto ? 0 : 0.5}
      overflow='hidden'
      cursor='default'
      // Una reserva cancelada nunca se pone por delante de una vigente, ni
      // aunque el ratón pase por encima: lo que importa ver es lo que sigue
      // en pie. Por eso se queda abajo y no sube al pasar el ratón.
      zIndex={cancelada ? 1 : 2}
      _hover={cancelada ? { shadow: 'sm' } : { shadow: 'md', zIndex: 3 }}
      // Una cancelada no ocupa la sala, así que tampoco debe estorbar para
      // elegir ese hueco: el ratón la atraviesa y llega a la franja de abajo.
      pointerEvents={cancelada ? 'none' : undefined}
      data-testid='tarjeta-reserva'
      data-id={bloque.id}
    >
      <Flex
        direction={botonConTexto ? 'column' : 'row'}
        align={botonConTexto ? 'stretch' : corto ? 'center' : 'flex-start'}
        h='100%'
        gap={1}
      >
        {/* El detalle va en el aviso del texto, no del botón: así al pasar
            por el botón se lee solo lo que hace. */}
        <Tooltip
          hasArrow
          placement='right'
          openDelay={250}
          label={
            <Box fontSize='xs' py={1}>
              <Text fontWeight='bold'>{bloque.cliente}</Text>
              {r.companyName && <Text>{r.companyName}</Text>}
              <Text>
                {bloque.horaInicio}
                {bloque.horaFin ? ` – ${bloque.horaFin}` : ''} · {bloque.personas}{' '}
                {bloque.personas === 1 ? 'persona' : 'personas'}
              </Text>
              {(r.email || telefono) && (
                <Text opacity={0.85}>{[r.email, telefono].filter(Boolean).join(' · ')}</Text>
              )}
              <Text>
                {bloque.code} · {c.nombre}
              </Text>
              {bloque.enConflicto && (
                <Text color='red.200'>⚠ Comparte horario con otra reserva</Text>
              )}
            </Box>
          }
        >
          <Box flex={botonConTexto ? '0 0 auto' : '1'} minW={0}>
            <Text
              fontSize='11px'
              fontWeight='semibold'
              color={c.texto}
              noOfLines={1}
              textDecoration={cancelada ? 'line-through' : undefined}
              lineHeight={corto ? `${Math.max(alto - 2, 14)}px` : '15px'}
            >
              {bloque.enConflicto && '⚠ '}
              {bloque.cliente}
            </Text>
            {!corto && (
              <Text fontSize='10px' color={c.texto} opacity={0.75} noOfLines={1}>
                {bloque.horaInicio}
                {bloque.horaFin ? `–${bloque.horaFin}` : ''} · {bloque.personas}p
              </Text>
            )}
          </Box>
        </Tooltip>
        {conAcciones && (
          <Box
            flexShrink={0}
            alignSelf={botonConTexto ? 'flex-start' : undefined}
            // La tarjeta cancelada deja pasar el ratón (para poder elegir el
            // hueco); sus botones, no: tienen que poder pulsarse.
            pointerEvents='auto'
          >
            <AccionesReserva
              reserva={r}
              compacto={!botonConTexto}
              onVerHistorial={onVerHistorial}
              onVerTarjeta={onVerTarjeta}
            />
          </Box>
        )}
      </Flex>
    </MotionBox>
  );
};

/* ──────────────────────  columna de día completo  ────────────────────── */

const ColumnaDiaCompleto = ({
  espacio,
  reservas,
  alto,
  fecha,
  onNueva,
  onVerHistorial,
  onVerTarjeta,
}: {
  espacio: EspacioAgenda;
  reservas: any[];
  alto: number;
  fecha: Date;
  /**
   * Nueva reserva en la zona, sabiendo cuántas sillas quedan. Solo llega
   * cuando se puede reservar (panel, y el día no ha pasado). Aquí no se elige
   * hora: la pone quien reserva, en el formulario.
   */
  onNueva?: (sillasLibres: number) => void;
  onVerHistorial?: (reserva: ReservaCruda) => void;
  onVerTarjeta?: (reserva: ReservaCruda) => void;
}) => {
  const { plazas, personas } = useMemo(
    () => ocupacionDiaCompleto(reservas, espacio.id),
    [reservas, espacio.id]
  );
  const porcentaje = espacio.cupo > 0 ? Math.min((plazas / espacio.cupo) * 100, 100) : 0;
  const color = porcentaje > 90 ? 'red' : porcentaje > 65 ? 'orange' : 'green';

  // Sillas que quedan según el mapa de ocupación de la sede (lo que mira el
  // formulario: en día completo cada reserva ocupa sus sillas en todas las
  // franjas, así que la más llena manda) y según las reservas a la vista. Se
  // toma lo más prudente de las dos para no prometer sillas que no hay.
  const sillasLibres = useMemo(() => {
    const marcas = espacio.ocupacion?.[format(fecha, 'yyyy-MM-dd')] ?? {};
    const enMapa = Math.max(0, ...Object.values(marcas).map((ids) => ids?.length ?? 0));
    return Math.max(0, espacio.cupo - Math.max(plazas, enMapa));
  }, [espacio.ocupacion, espacio.cupo, fecha, plazas]);

  return (
    <Box
      height={`${alto}px`}
      borderLeftWidth='1px'
      borderColor='gray.200'
      bg='gray.50'
      px={2}
      py={2}
    >
      <HStack spacing={2} mb={1}>
        <Icon as={FiUsers} color={`${color}.500`} boxSize={3.5} />
        <Text fontSize='xs' fontWeight='bold' color={`${color}.600`}>
          {plazas} / {espacio.cupo}
        </Text>
        <Text fontSize='10px' color='gray.500'>
          sillas
        </Text>
      </HStack>
      <Box h='4px' bg='gray.200' borderRadius='full' mb={2} overflow='hidden'>
        <MotionBox
          h='100%'
          bg={`${color}.400`}
          initial={{ width: 0 }}
          animate={{ width: `${porcentaje}%` }}
          transition={{ duration: 0.5 }}
        />
      </Box>
      {onNueva && (
        <Button
          size='xs'
          w='100%'
          mb={2}
          variant='outline'
          colorScheme='blue'
          bg='white'
          leftIcon={<FiPlus />}
          isDisabled={sillasLibres <= 0}
          onClick={() => onNueva(sillasLibres)}
          data-testid='nueva-dia-completo'
        >
          {sillasLibres > 0 ? 'Nueva reserva' : 'Sin sillas libres'}
        </Button>
      )}
      <VStack
        align='stretch'
        spacing={1}
        overflowY='auto'
        maxH={`${alto - 62 - (onNueva ? 32 : 0)}px`}
        pr={1}
      >
        {personas.length === 0 && (
          <Text fontSize='xs' color='gray.400' textAlign='center' pt={4}>
            Sin reservas
          </Text>
        )}
        {personas.map((p) => {
          const c = COLORES[p.estado] ?? COLORES.pending;
          return (
            <HStack
              key={p.id}
              spacing={1.5}
              bg={c.bg}
              borderLeftWidth='2px'
              borderLeftColor={c.borde}
              borderRadius='3px'
              px={1.5}
              py='2px'
            >
              {/* Con tres iconos al lado el nombre se recorta; el detalle
                  entero va en el aviso al pasar por él. */}
              <Tooltip
                hasArrow
                placement='left'
                openDelay={250}
                label={
                  <Box fontSize='xs' py={1}>
                    <Text fontWeight='bold'>{p.cliente}</Text>
                    {p.reserva.companyName && <Text>{p.reserva.companyName}</Text>}
                    <Text>
                      {p.hora ? `Llega ${p.hora} · ` : ''}
                      {p.personas} {p.personas === 1 ? 'persona' : 'personas'}
                    </Text>
                    <Text>
                      {p.code} · {c.nombre}
                    </Text>
                  </Box>
                }
              >
                <Text fontSize='11px' color={c.texto} noOfLines={1} flex={1}>
                  {p.cliente}
                </Text>
              </Tooltip>
              {p.personas > 1 && (
                <Badge fontSize='9px' colorScheme='gray' px={1}>
                  {p.personas}
                </Badge>
              )}
              <AccionesReserva
                reserva={p.reserva}
                compacto
                onVerHistorial={onVerHistorial}
                onVerTarjeta={onVerTarjeta}
              />
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
};

/* ────────────────────────────  vista día  ──────────────────────────── */

const VistaDia = ({
  companyId,
  fecha,
  onHuecoElegido,
  onVerHistorial,
  onVerTarjeta,
}: {
  companyId?: string;
  fecha: Date;
  onHuecoElegido?: (hueco: HuecoLibre) => void;
  onVerHistorial?: (reserva: ReservaCruda) => void;
  onVerTarjeta?: (reserva: ReservaCruda) => void;
}) => {
  const {
    espacios,
    cargando: cargandoEspacios,
    desdeCache: espaciosDeCache,
    error: errorEspacios,
    reintentar: reintentarEspacios,
  } = useEspacios(companyId);
  const {
    reservas,
    cargando,
    desdeCache: reservasDeCache,
    error: errorReservas,
    reintentar: reintentarReservas,
  } = useReservasEnRango(companyId, startOfDay(fecha), endOfDay(fecha));
  const sinConexion = espaciosDeCache || reservasDeCache;
  const reintentarTodo = () => {
    reintentarEspacios();
    reintentarReservas();
  };
  const rejilla = useRejilla(espacios, fecha);
  const cargandoAlgo = cargandoEspacios || (cargando && reservas.length === 0);
  const { altoFila, disponible, contenedor, cabecera } = useAltoFila(rejilla.filas, !cargandoAlgo);
  const ahora = useMinutoActual(isToday(fecha));

  const bloques = useMemo(
    () =>
      armarBloques({
        reservas,
        espacios,
        inicioRejilla: rejilla.inicio,
        totalFilas: rejilla.filas,
        // Las reservas sin hora de fin duran lo que diga su espacio.
        minutosBloqueoPorEspacio: Object.fromEntries(espacios.map((e) => [e.id, e.bloqueo])),
      }),
    [reservas, espacios, rejilla.inicio, rejilla.filas]
  );

  const minutosAhora = ahora.getHours() * 60 + ahora.getMinutes();

  /**
   * Primera franja que todavía se puede reservar.
   *
   * En el pasado no se reserva: el formulario no ofrece horas que ya pasaron,
   * así que dejar elegirlas aquí llevaba a un formulario con la hora en blanco
   * sin explicar por qué. Hoy se corta en la hora actual; en días anteriores no
   * hay nada que elegir.
   */
  const primeraFilaUtil = useMemo(() => {
    const hoy = isToday(fecha);
    if (hoy) {
      return Math.max(0, Math.ceil((minutosAhora - rejilla.inicio) / MINUTOS_POR_FILA));
    }
    return fecha < startOfDay(new Date()) ? rejilla.filas : 0;
  }, [fecha, minutosAhora, rejilla.inicio, rejilla.filas]);

  /**
   * Filas que no se pueden elegir, por espacio: ocupadas, ya pasadas y, en
   * las visitas, las que no caben según su configuración (ver
   * `filasSinSitioParaVisita`). Se calcula una vez por espacio, no por celda.
   */
  const bloqueadasPorEspacio = useMemo(() => {
    const mapa: Record<string, Set<number>> = {};
    espacios.forEach((e) => {
      const bloqueadas = filasOcupadas(bloques[e.id]);
      if (esVisita(e)) filasSinSitioParaVisita(e, fecha, rejilla).forEach((f) => bloqueadas.add(f));
      for (let f = 0; f < primeraFilaUtil; f++) bloqueadas.add(f);
      mapa[e.id] = bloqueadas;
    });
    return mapa;
  }, [espacios, bloques, fecha, rejilla, primeraFilaUtil]);

  const ocupadasPorEspacio = useCallback(
    (espacioId: string) => bloqueadasPorEspacio[espacioId] ?? new Set<number>(),
    [bloqueadasPorEspacio]
  );

  /** En las visitas la duración la pone la configuración: un clic, no un arrastre. */
  const filasFijasPorEspacio = useCallback(
    (espacioId: string) => {
      const e = espacios.find((x) => x.id === espacioId);
      return e && esVisita(e) ? Math.max(1, Math.round(e.bloqueo / MINUTOS_POR_FILA)) : undefined;
    },
    [espacios]
  );

  const { seleccion, arrastrando, empezar, extender, limpiar } = useSeleccionFranjas({
    activo: Boolean(onHuecoElegido),
    totalFilas: rejilla.filas,
    ocupadasPorEspacio,
    filasFijasPorEspacio,
    onElegido: ({ espacioId, filaInicio, filaFin }) => {
      const espacio = espacios.find((e) => e.id === espacioId);
      if (!espacio || !onHuecoElegido) return;
      const horaInicio = rejilla.horas[filaInicio];
      // La hora de fin es el principio de la franja siguiente a la última
      // elegida: media hora arrastrada son 09:00–09:30, no 09:00–09:00.
      const horaFin = rejilla.horas[filaFin + 1] ?? sumarMediaHora(rejilla.horas[filaFin]);
      limpiar();
      onHuecoElegido({
        espacio,
        fecha,
        horaInicio,
        horaFin: espacio.conHoraFin ? horaFin : undefined,
        duracionMinutos: esVisita(espacio) ? espacio.bloqueo : undefined,
      });
    },
  });

  const alto = rejilla.filas * altoFila;
  const mostrarAhora =
    isToday(fecha) && minutosAhora >= rejilla.inicio && minutosAhora <= rejilla.fin;
  const topAhora = ((minutosAhora - rejilla.inicio) / MINUTOS_POR_FILA) * altoFila;

  const errorCarga = errorEspacios ?? errorReservas;
  if (errorCarga) {
    return <AvisoDeCarga mensaje={errorCarga} onReintentar={reintentarTodo} />;
  }

  if (cargandoAlgo) {
    return (
      <Box position='relative'>
        {sinConexion && <AvisoConexion onReintentar={reintentarTodo} />}
        <VStack align='stretch' spacing={2} p={4}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} h='38px' borderRadius='md' />
          ))}
        </VStack>
      </Box>
    );
  }

  return (
    <Box position='relative'>
      {sinConexion && <AvisoConexion onReintentar={reintentarTodo} />}
      <Box
        ref={contenedor}
        overflowX='auto'
        overflowY='auto'
        maxH={disponible ? `${disponible}px` : 'calc(100vh - 190px)'}
        position='relative'
      >
        <Box minW={`${ANCHO_HORAS + espacios.length * ANCHO_COLUMNA}px`}>
          {/* Cabecera */}
          <Flex
            ref={cabecera}
            position='sticky'
            top={0}
            zIndex={5}
            bg='white'
            borderBottomWidth='2px'
            borderColor='gray.200'
          >
            <Box
              w={`${ANCHO_HORAS}px`}
              flexShrink={0}
              position='sticky'
              left={0}
              bg='white'
              zIndex={6}
            />
            {espacios.map((e) => (
              <Box
                key={e.id}
                w={`${ANCHO_COLUMNA}px`}
                flexShrink={0}
                px={2}
                py={2}
                borderLeftWidth='1px'
                borderColor='gray.200'
                bg={e.diaCompleto ? 'gray.50' : 'white'}
              >
                <Text fontSize='xs' fontWeight='bold' noOfLines={1} title={e.nombre}>
                  {e.nombre}
                </Text>
                <Text fontSize='10px' color='gray.500'>
                  {e.diaCompleto ? `día completo · ${e.cupo} sillas` : `${e.apertura}–${e.cierre}`}
                </Text>
              </Box>
            ))}
          </Flex>

          {/* Rejilla */}
          <Flex position='relative'>
            {/* Columna de horas, siempre visible */}
            <Box
              w={`${ANCHO_HORAS}px`}
              flexShrink={0}
              position='sticky'
              left={0}
              bg='white'
              zIndex={4}
            >
              {rejilla.horas.map((h) => (
                <Box
                  key={h}
                  h={`${altoFila}px`}
                  borderBottomWidth='1px'
                  borderColor={h.endsWith(':00') ? 'gray.200' : 'gray.100'}
                  pr={2}
                >
                  {h.endsWith(':00') && (
                    <Text
                      fontSize='10px'
                      color='gray.500'
                      textAlign='right'
                      lineHeight={`${altoFila}px`}
                    >
                      {h}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>

            {/* Una sola pasada, en el orden que fija `useEspacios`: si se separaran
              por tipo, "Conocer el Espacio" adelantaría a la zona de coworking. */}
            {espacios.map((e) =>
              e.diaCompleto ? (
                <Box key={e.id} w={`${ANCHO_COLUMNA}px`} flexShrink={0}>
                  <ColumnaDiaCompleto
                    espacio={e}
                    reservas={reservas}
                    alto={alto}
                    fecha={fecha}
                    onNueva={
                      onHuecoElegido && primeraFilaUtil < rejilla.filas
                        ? (sillasLibres) => onHuecoElegido({ espacio: e, fecha, sillasLibres })
                        : undefined
                    }
                    onVerHistorial={onVerHistorial}
                    onVerTarjeta={onVerTarjeta}
                  />
                </Box>
              ) : (
                <Box
                  key={e.id}
                  w={`${ANCHO_COLUMNA}px`}
                  flexShrink={0}
                  position='relative'
                  borderLeftWidth='1px'
                  borderColor='gray.200'
                  sx={arrastrando ? { userSelect: 'none' } : undefined}
                >
                  {rejilla.horas.map((h, fila) => {
                    const tomada = ocupadasPorEspacio(e.id).has(fila);
                    const pasada = fila < primeraFilaUtil;
                    // En las visitas, una hora en la que no cabe el bloque se
                    // ve como no disponible aunque no haya nada pintado encima.
                    const sinSitio = tomada && esVisita(e);
                    return (
                      <Box
                        key={h}
                        h={`${altoFila}px`}
                        borderBottomWidth='1px'
                        borderColor={h.endsWith(':00') ? 'gray.200' : 'gray.100'}
                        bg={pasada || sinSitio ? 'gray.50' : undefined}
                        cursor={onHuecoElegido && !tomada ? 'cell' : 'default'}
                        _hover={
                          onHuecoElegido && !tomada && !arrastrando ? { bg: 'blue.50' } : undefined
                        }
                        transition='background-color .08s'
                        onMouseDown={(ev) => {
                          if (!onHuecoElegido || tomada) return;
                          ev.preventDefault();
                          empezar(e.id, fila);
                        }}
                        onMouseEnter={() => extender(e.id, fila)}
                      />
                    );
                  })}

                  {/* Lo que se está eligiendo ahora mismo */}
                  {seleccion?.espacioId === e.id && (
                    <Box
                      position='absolute'
                      left='2px'
                      right='2px'
                      top={`${seleccion.desde * altoFila}px`}
                      height={`${(seleccion.hasta - seleccion.desde + 1) * altoFila - 2}px`}
                      bg='blue.100'
                      borderWidth='2px'
                      borderColor='blue.400'
                      borderRadius='4px'
                      zIndex={4}
                      pointerEvents='none'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      boxShadow='sm'
                    >
                      <Text fontSize='11px' fontWeight='bold' color='blue.700' noOfLines={1}>
                        {rejilla.horas[seleccion.desde]}–
                        {rejilla.horas[seleccion.hasta + 1] ??
                          sumarMediaHora(rejilla.horas[seleccion.hasta])}
                      </Text>
                    </Box>
                  )}
                  <AnimatePresence initial={false}>
                    {(bloques[e.id] ?? []).map((b, i) => (
                      <Bloque
                        key={b.id}
                        bloque={b}
                        indice={i}
                        altoFila={altoFila}
                        onVerHistorial={onVerHistorial}
                        onVerTarjeta={onVerTarjeta}
                      />
                    ))}
                  </AnimatePresence>
                </Box>
              )
            )}

            {/* Línea de la hora actual */}
            {mostrarAhora && (
              <Box
                position='absolute'
                left={0}
                right={0}
                top={`${topAhora}px`}
                height='2px'
                bg='red.400'
                zIndex={3}
                pointerEvents='none'
              >
                <Circle
                  size='8px'
                  bg='red.400'
                  position='absolute'
                  left={`${ANCHO_HORAS - 4}px`}
                  top='-3px'
                />
              </Box>
            )}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

/* ───────────────────────────  vista semana  ─────────────────────────── */

const VistaSemana = ({ companyId, fecha }: { companyId?: string; fecha: Date }) => {
  const {
    espacios,
    desdeCache: espaciosDeCache,
    error: errorEspacios,
    reintentar: reintentarEspacios,
  } = useEspacios(companyId);
  const { contenedor, disponible } = useAltoDisponible(true);
  const [salaId, setSalaId] = useState<string>('');
  const inicio = startOfWeek(fecha, { weekStartsOn: 1 });
  const fin = endOfWeek(fecha, { weekStartsOn: 1 });
  const {
    reservas,
    desdeCache: reservasDeCache,
    error: errorReservas,
    reintentar: reintentarReservas,
  } = useReservasEnRango(companyId, startOfDay(inicio), endOfDay(fin));
  const reintentarTodo = () => {
    reintentarEspacios();
    reintentarReservas();
  };

  const sala = espacios.find((e) => e.id === salaId) ?? espacios[0];
  const dias = Array.from({ length: 7 }, (_, i) => addDays(inicio, i));

  const porDia = useMemo(() => {
    const mapa: Record<string, any[]> = {};
    dias.forEach((d) => (mapa[format(d, 'yyyy-MM-dd')] = []));
    reservas.forEach((r: any) => {
      if (!sala || r.location?.id !== sala.id) return;
      const f = r.date ?? '';
      if (mapa[f]) mapa[f].push(r);
    });
    return mapa;
  }, [reservas, sala?.id, inicio.getTime()]);

  const errorCarga = errorEspacios ?? errorReservas;
  if (errorCarga) {
    return <AvisoDeCarga mensaje={errorCarga} onReintentar={reintentarTodo} />;
  }

  return (
    <Box p={3} position='relative'>
      {(espaciosDeCache || reservasDeCache) && <AvisoConexion onReintentar={reintentarTodo} />}
      <HStack mb={3} spacing={3}>
        <Text fontSize='sm' color='gray.600'>
          Espacio
        </Text>
        <Select
          size='sm'
          maxW='260px'
          value={sala?.id ?? ''}
          onChange={(e) => setSalaId(e.target.value)}
        >
          {espacios.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nombre}
            </option>
          ))}
        </Select>
        <Text fontSize='xs' color='gray.500'>
          Una semana entera de un solo espacio: con once a la vez no se leería nada.
        </Text>
      </HStack>

      <Flex ref={contenedor} gap={2} overflowX='auto'>
        {dias.map((d) => {
          const lista = porDia[format(d, 'yyyy-MM-dd')] ?? [];
          const hoy = isToday(d);
          return (
            <Box
              key={d.toISOString()}
              flex='1 0 150px'
              borderWidth='1px'
              borderColor={hoy ? 'blue.300' : 'gray.200'}
              bg={hoy ? 'blue.50' : 'white'}
              borderRadius='md'
              p={2}
              minH={disponible ? `${disponible}px` : '260px'}
              display='flex'
              flexDirection='column'
            >
              <Text fontSize='xs' fontWeight='bold' color={hoy ? 'blue.600' : 'gray.700'}>
                {format(d, 'EEE d', { locale: esLocale })}
              </Text>
              <Text fontSize='10px' color='gray.500' mb={2}>
                {lista.filter((r) => r.status !== 'cancelled').length} reservas
              </Text>
              <VStack align='stretch' spacing={1} overflowY='auto' flex={1}>
                {lista
                  .sort((a, b) => (a.startHour ?? '').localeCompare(b.startHour ?? ''))
                  .map((r) => {
                    const c = COLORES[(r.status ?? 'pending') as EstadoReserva] ?? COLORES.pending;
                    return (
                      <Box
                        key={r.id}
                        bg={c.bg}
                        borderLeftWidth='2px'
                        borderLeftColor={c.borde}
                        borderRadius='3px'
                        px={1.5}
                        py='2px'
                      >
                        <Text fontSize='10px' fontWeight='semibold' color={c.texto}>
                          {r.startHour ?? ''}
                          {r.endHour && r.endHour !== '--' ? `–${r.endHour}` : ''}
                        </Text>
                        <Text fontSize='10px' color={c.texto} noOfLines={1}>
                          {r.namesAndSurnames ?? r.name ?? ''}
                        </Text>
                      </Box>
                    );
                  })}
              </VStack>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

/* ────────────────────────────  vista mes  ──────────────────────────── */

const VistaMes = ({
  companyId,
  fecha,
  onElegirDia,
}: {
  companyId?: string;
  fecha: Date;
  onElegirDia: (d: Date) => void;
}) => {
  const { contenedor, disponible } = useAltoDisponible(true);
  const inicio = startOfWeek(startOfMonth(fecha), { weekStartsOn: 1 });
  const fin = endOfWeek(endOfMonth(fecha), { weekStartsOn: 1 });
  const {
    reservas,
    desdeCache: reservasDeCache,
    error: errorReservas,
    reintentar: reintentarReservas,
  } = useReservasEnRango(companyId, startOfDay(inicio), endOfDay(fin));

  const porDia = useMemo(() => {
    const mapa: Record<string, { total: number; personas: number }> = {};
    reservas.forEach((r: any) => {
      if (r.status === 'cancelled') return;
      const f = r.date ?? '';
      if (!mapa[f]) mapa[f] = { total: 0, personas: 0 };
      mapa[f].total++;
      mapa[f].personas += Math.max(Number(r.numberPeople ?? 1), 1);
    });
    return mapa;
  }, [reservas]);

  const celdas: Date[] = [];
  for (let d = inicio; d <= fin; d = addDays(d, 1)) celdas.push(d);

  // Las semanas se reparten lo que queda de pantalla; si el mes es corto no se
  // estiran sin límite, y si la ventana es baja nunca bajan de lo legible.
  const semanas = Math.max(Math.ceil(celdas.length / 7), 1);
  const altoCelda = disponible
    ? Math.min(Math.max(Math.floor(disponible / semanas) - 8, 72), 180)
    : 72;

  if (errorReservas) {
    return <AvisoDeCarga mensaje={errorReservas} onReintentar={reintentarReservas} />;
  }

  return (
    <Box p={4} position='relative'>
      {reservasDeCache && <AvisoConexion onReintentar={reintentarReservas} />}
      <Flex mb={2}>
        {['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d) => (
          <Text
            key={d}
            flex={1}
            fontSize='xs'
            color='gray.500'
            textAlign='center'
            fontWeight='semibold'
          >
            {d}
          </Text>
        ))}
      </Flex>
      <Flex ref={contenedor} wrap='wrap'>
        {celdas.map((d) => {
          const clave = format(d, 'yyyy-MM-dd');
          const datos = porDia[clave];
          const esteMes = d.getMonth() === fecha.getMonth();
          const hoy = isToday(d);
          return (
            <Box
              key={clave}
              w='calc(100% / 7)'
              p={1}
              onClick={() => onElegirDia(d)}
              cursor='pointer'
              role='button'
            >
              <MotionBox
                whileHover={{ scale: 1.03 }}
                borderWidth='1px'
                borderColor={hoy ? 'blue.400' : 'gray.200'}
                bg={!esteMes ? 'gray.50' : hoy ? 'blue.50' : 'white'}
                borderRadius='md'
                p={2}
                h={`${altoCelda}px`}
                opacity={esteMes ? 1 : 0.5}
              >
                <Text
                  fontSize='xs'
                  fontWeight={hoy ? 'bold' : 'normal'}
                  color={hoy ? 'blue.600' : 'gray.700'}
                >
                  {format(d, 'd')}
                </Text>
                {datos && (
                  <VStack align='stretch' spacing={0} mt={1}>
                    <Text fontSize='10px' color='blue.600' fontWeight='semibold'>
                      {datos.total} reservas
                    </Text>
                    <Text fontSize='10px' color='gray.500'>
                      {datos.personas} personas
                    </Text>
                  </VStack>
                )}
              </MotionBox>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

/* ────────────────────────────  el modal  ──────────────────────────── */

const CalendarioReservas = ({
  isOpen,
  onClose,
  companyId,
  onHuecoElegido,
  fechaInicial,
}: Props) => {
  const [fecha, setFecha] = useState(() => startOfDay(fechaInicial ?? new Date()));
  const [vista, setVista] = useState<'dia' | 'semana' | 'mes'>('dia');

  /** Hueco recién elegido, a la espera de saber cuánta gente viene. */
  const [hueco, setHueco] = useState<HuecoLibre | null>(null);
  /** Reserva cuyo historial se está viendo en el cajón lateral. */
  const [reservaDelHistorial, setReservaDelHistorial] = useState<ReservaCruda | null>(null);
  /** Reserva cuya tarjeta de confirmación se está viendo. */
  const [reservaDeLaTarjeta, setReservaDeLaTarjeta] = useState<ReservaCruda | null>(null);
  /** Hacia dónde se navegó por última vez: 1 adelante, -1 atrás. */
  const [sentido, setSentido] = useState(1);

  // El calendario vive montado mientras dura la sesión, así que su fecha no
  // puede fijarse una sola vez: si se fijara, tras medianoche seguiría
  // abriendo en "ayer". Cada apertura arranca en el día de la tabla de fuera
  // (o en hoy si no lo hay), en vista de día. Se ajusta durante el render,
  // no en un efecto: así el día viejo no llega a pintarse ni a pedir sus
  // reservas ni por un instante.
  const [estabaAbierto, setEstabaAbierto] = useState(isOpen);
  if (isOpen !== estabaAbierto) {
    setEstabaAbierto(isOpen);
    if (isOpen) {
      setFecha(startOfDay(fechaInicial ?? new Date()));
      setVista('dia');
      setSentido(1);
    }
  }

  const mover = (pasos: number) => {
    setSentido(pasos >= 0 ? 1 : -1);
    if (vista === 'dia') setFecha((f) => addDays(f, pasos));
    else if (vista === 'semana') setFecha((f) => addDays(f, pasos * 7));
    else setFecha((f) => addMonths(f, pasos));
  };

  // La ventanita para elegir día, colgada del título.
  const selectorFecha = useDisclosure();

  /**
   * Saltar a un día cualquiera.
   *
   * Se calcula hacia dónde va el salto para que la transición siga contando
   * lo mismo que las flechas: hacia delante en el tiempo, entra por la
   * derecha; hacia atrás, por la izquierda.
   */
  const irAFecha = (nueva: Date) => {
    setSentido(startOfDay(nueva) >= startOfDay(fecha) ? 1 : -1);
    setFecha(nueva);
  };

  const titulo =
    vista === 'dia'
      ? format(fecha, "EEEE d 'de' MMMM 'de' yyyy", { locale: esLocale })
      : vista === 'semana'
      ? `${format(startOfWeek(fecha, { weekStartsOn: 1 }), 'd MMM', {
          locale: esLocale,
        })} – ${format(endOfWeek(fecha, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: esLocale })}`
      : format(fecha, "MMMM 'de' yyyy", { locale: esLocale });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='full' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent m={{ base: 0, md: 3 }} borderRadius={{ base: 0, md: 'lg' }}>
        <ModalHeader pb={2} pt={3}>
          <Flex align='center' gap={3} wrap='wrap'>
            <HStack spacing={2}>
              <Text fontSize='md' fontWeight='bold'>
                Calendario
              </Text>
              <MarcaBeta />
            </HStack>

            <HStack spacing={1}>
              <IconButton
                size='xs'
                variant='ghost'
                aria-label='Anterior'
                icon={<FiChevronLeft />}
                onClick={() => mover(-1)}
              />
              <Button size='xs' variant='outline' onClick={() => irAFecha(new Date())}>
                Hoy
              </Button>
              <IconButton
                size='xs'
                variant='ghost'
                aria-label='Siguiente'
                icon={<FiChevronRight />}
                onClick={() => mover(1)}
              />
            </HStack>

            {/* El propio título abre el calendario. Es el sitio donde ya
                está mirando quien quiere cambiar de día, y es el objetivo más
                grande de la barra; la flecha hacia abajo avisa de que se
                puede pulsar. Las flechas y "Hoy" siguen igual para moverse
                de uno en uno. */}
            <Popover
              isOpen={selectorFecha.isOpen}
              onOpen={selectorFecha.onOpen}
              onClose={selectorFecha.onClose}
              placement='bottom-start'
              isLazy
            >
              <PopoverTrigger>
                <Button
                  size='sm'
                  variant='ghost'
                  px={2}
                  minW='250px'
                  justifyContent='space-between'
                  fontWeight='normal'
                  fontSize='sm'
                  color='gray.600'
                  rightIcon={<Icon as={FiChevronDown} boxSize={3.5} />}
                  aria-label='Elegir otro día'
                >
                  {/* Solo la primera letra: 'capitalize' ponía mayúscula a
                      cada palabra y salía "Jueves 10 De Septiembre De 2026".
                      Necesita ser un bloque para que ::first-letter aplique. */}
                  <Text
                    as='span'
                    display='inline-block'
                    sx={{ '&::first-letter': { textTransform: 'uppercase' } }}
                  >
                    {titulo}
                  </Text>
                </Button>
              </PopoverTrigger>
              {/* Portal: el calendario vive dentro de una modal a pantalla
                  completa con desplazamiento propio, y sin esto se recortaba
                  contra el borde. */}
              <Portal>
                <PopoverContent w='auto' boxShadow='xl' _focusVisible={{ outline: 'none' }}>
                  <PopoverArrow />
                  <PopoverBody p={0}>
                    <Calendar
                      locale={esLocale}
                      date={fecha}
                      onChange={(elegida: Date) => {
                        irAFecha(elegida);
                        selectorFecha.onClose();
                      }}
                      className='calendarElement'
                    />
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>

            <ButtonGroup size='xs' isAttached variant='outline'>
              {(['dia', 'semana', 'mes'] as const).map((v) => (
                <Button
                  key={v}
                  onClick={() => setVista(v)}
                  colorScheme={vista === v ? 'blue' : 'gray'}
                  variant={vista === v ? 'solid' : 'outline'}
                  textTransform='capitalize'
                >
                  {v === 'dia' ? 'Día' : v}
                </Button>
              ))}
            </ButtonGroup>

            <HStack spacing={3} ml='auto' pr={8} display={{ base: 'none', lg: 'flex' }}>
              {(Object.keys(COLORES) as EstadoReserva[]).map((e) => (
                <HStack key={e} spacing={1}>
                  <Box
                    w='9px'
                    h='9px'
                    borderRadius='2px'
                    bg={COLORES[e].bg}
                    borderWidth='2px'
                    borderColor={COLORES[e].borde}
                  />
                  <Text fontSize='10px' color='gray.600'>
                    {COLORES[e].nombre}
                  </Text>
                </HStack>
              ))}
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0}>
          {/* Cambiar de día o de vista no debe ser un corte seco: el contenido
              entra desplazándose un poco hacia el lado al que se navega, que es
              lo que hace entender de un vistazo que te has movido en el tiempo. */}
          <AnimatePresence exitBeforeEnter initial={false} custom={sentido}>
            <MotionBox
              key={`${vista}-${format(fecha, 'yyyy-MM-dd')}`}
              custom={sentido}
              initial={{ opacity: 0, x: sentido * 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: sentido * -18 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
            >
              {vista === 'dia' && (
                <VistaDia
                  companyId={companyId}
                  fecha={fecha}
                  onHuecoElegido={onHuecoElegido ? setHueco : undefined}
                  onVerHistorial={setReservaDelHistorial}
                  onVerTarjeta={setReservaDeLaTarjeta}
                />
              )}
              {vista === 'semana' && <VistaSemana companyId={companyId} fecha={fecha} />}
              {vista === 'mes' && (
                <VistaMes
                  companyId={companyId}
                  fecha={fecha}
                  onElegirDia={(d) => {
                    setFecha(d);
                    setVista('dia');
                  }}
                />
              )}
            </MotionBox>
          </AnimatePresence>
        </ModalBody>

        {/* Las ventanas que se abren ENCIMA del calendario van dentro de su
            contenido, no al lado: si cuelgan fuera, el bloqueo de scroll de
            la modal de abajo (react-remove-scroll) toma la rueda del ratón
            sobre la de arriba como "fuera" y la cancela: no se podía hacer
            scroll con la rueda ni con el trackpad, solo con la barra. */}
        <CuantasPersonas
          isOpen={Boolean(hueco)}
          onClose={() => setHueco(null)}
          hueco={
            hueco
              ? {
                  salaNombre: hueco.espacio.nombre,
                  salaId: hueco.espacio.id,
                  capacidad: hueco.espacio.capacidad || undefined,
                  cupo: hueco.espacio.cupo,
                  porPersona: hueco.espacio.porPersona,
                  fecha: hueco.fecha,
                  horaInicio: hueco.horaInicio,
                  horaFin: hueco.horaFin,
                  duracionMinutos: hueco.duracionMinutos,
                  sillasLibres: hueco.sillasLibres,
                }
              : null
          }
          onConfirmar={(personas) => {
            if (!hueco) return;
            setHueco(null);
            onHuecoElegido?.({ ...hueco, personas });
          }}
        />

        {/* El mismo cajón de historial de la tabla, sobre el calendario. */}
        <HistorialReserva
          reserva={reservaDelHistorial}
          isOpen={Boolean(reservaDelHistorial)}
          onClose={() => setReservaDelHistorial(null)}
        />

        {/* Y la misma tarjeta de confirmación de la tabla. */}
        <TarjetaReserva
          reserva={reservaDeLaTarjeta}
          isOpen={Boolean(reservaDeLaTarjeta)}
          onClose={() => setReservaDeLaTarjeta(null)}
        />
      </ModalContent>
    </Modal>
  );
};

export default CalendarioReservas;
